<template>
  <div class="ctx-tab">
    <section class="ctx-drawer__card" aria-labelledby="ctx-statuses-heading">
      <div class="ctx-drawer__card-head">
        <span class="ctx-drawer__card-icon" aria-hidden="true">
          <v-icon icon="mdi-list-status" size="18" />
        </span>
        <h3 id="ctx-statuses-heading" class="ctx-drawer__card-title">
          Lead statuses
          <span class="ctx-drawer__card-count">{{ leadStatuses.length }}</span>
        </h3>
      </div>

      <p class="ctx-drawer__block-label ctx-drawer__statuses-hint">
        Statuses are specific to this product — pickers on the Leads
        screen use this list. PENDING can't be removed — it's the
        platform's built-in starting status for every new lead.
      </p>

      <div v-if="leadStatuses.length" class="ctx-drawer__statuses">
        <span v-for="status in leadStatuses" :key="status" class="ctx-drawer__status-chip">
          {{ status }}
          <button
            v-if="leadStatuses.length > 1 && status !== 'PENDING'"
            type="button"
            class="ctx-drawer__status-remove"
            :aria-label="`Remove status ${status}`"
            :disabled="statusWorking"
            @click="removeStatus(status)"
          >
            <v-icon icon="mdi-close" size="12" />
          </button>
        </span>
      </div>
      <p v-else class="ctx-drawer__empty">
        No statuses returned yet — leads validate against the default
        list (PENDING, CONTACTED, CONVERTED, REJECTED).
      </p>

      <div class="ctx-drawer__field-form-row ctx-drawer__status-form">
        <v-text-field
          v-model="newStatus"
          placeholder="New status, e.g. FOLLOW_UP"
          variant="outlined"
          density="compact"
          hide-details
          class="ctx-drawer__field-input"
          @keydown.enter.prevent="addStatus"
        />
        <v-btn
          variant="flat"
          size="small"
          class="ctx-drawer__field-save"
          :loading="statusWorking"
          :disabled="!String(newStatus).trim()"
          @click="addStatus"
        >
          Add
        </v-btn>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { addContextLeadStatuses, removeContextLeadStatuses } from '@/api/contextsApi'
import { getApiMessage, isApiSuccess } from '@/mixins/Extras/ApiResponse'
import { normalizeLeadStatuses } from '@/utils/contexts'
import '@/styles/contexts/info-drawer.css'

defineOptions({ name: 'ContextDispositionsTab' })

const props = defineProps({
  context: { type: Object, default: null },
})

const emit = defineEmits(['saved', 'error'])

// Local override, same reasoning as ContextFormFieldsTab.vue's own --
// these two tabs never cross-talk, so each carries its own copy rather
// than sharing one.
const statusesOverride = ref(null)
const newStatus = ref('')
const statusWorking = ref(false)

const leadStatuses = computed(() => {
  const source = statusesOverride.value ?? props.context?.lead_statuses
  return normalizeLeadStatuses(source)
})

function applyStatusesResponse(response, fallbackList) {
  const returned = response?.data?.statuses
  statusesOverride.value = Array.isArray(returned) ? returned : fallbackList
}

async function addStatus() {
  const status = String(newStatus.value ?? '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '_')
  if (!status) return

  statusWorking.value = true
  try {
    const response = await addContextLeadStatuses({
      context_id: props.context?.context_id,
      statuses: [status],
    })
    if (!isApiSuccess(response)) {
      emit('error', getApiMessage(response, 'Failed to add status'))
      return
    }
    applyStatusesResponse(response, [...leadStatuses.value, status])
    newStatus.value = ''
    emit('saved', getApiMessage(response, `Status "${status}" added.`))
  } finally {
    statusWorking.value = false
  }
}

async function removeStatus(status) {
  statusWorking.value = true
  try {
    const response = await removeContextLeadStatuses({
      context_id: props.context?.context_id,
      statuses: [status],
    })
    if (!isApiSuccess(response)) {
      // Server blocks removing statuses still in use or the last one.
      emit('error', getApiMessage(response, 'Failed to remove status'))
      return
    }
    applyStatusesResponse(response, leadStatuses.value.filter((item) => item !== status))
    emit('saved', getApiMessage(response, `Status "${status}" removed.`))
  } finally {
    statusWorking.value = false
  }
}

watch(
  () => props.context?.context_id,
  () => {
    statusesOverride.value = null
    newStatus.value = ''
  },
)
</script>

<style scoped>
.ctx-tab {
  max-width: 42rem;
}
</style>
